import * as THREE from 'three'
import { sampleBeltRoute } from '../Game/beltPaths.js'
import { TUNING } from '../Game/definitions.js'

const pointKey = (_point) => `${_point.x.toFixed(3)},${_point.y.toFixed(3)}`

export default class BeltView
{
    constructor(_options)
    {
        this.belt = _options.belt
        this.factory = _options.factory
        this.definition = _options.state.logistics.getDefinition(this.belt.id)
        this.routes = _options.state.logistics.getRoutes(this.belt)
        this.container = new THREE.Group()
        this.straights = []
        this.itemViews = new Map()
        this.travel = 0
        const modules = new Set()

        for(const route of this.routes)
        {
            for(let segmentIndex = 0; segmentIndex < route.segments.length; segmentIndex++)
            {
                const segment = route.segments[segmentIndex]
                const key = `${pointKey(segment.from)}>${pointKey(segment.to)}`
                if(modules.has(key))
                    continue
                modules.add(key)
                const directionX = (segment.to.x - segment.from.x) / segment.length
                const directionY = (segment.to.y - segment.from.y) / segment.length
                const trimStart = segmentIndex === 0 ? 0 : 0.41
                const trimEnd = segmentIndex === route.segments.length - 1 ? 0 : 0.41
                const from = { x: segment.from.x + directionX * trimStart, y: segment.from.y + directionY * trimStart }
                const to = { x: segment.to.x - directionX * trimEnd, y: segment.to.y - directionY * trimEnd }
                const length = segment.length - trimStart - trimEnd
                const straight = this.factory.createBeltStraight(length)
                straight.position.set((from.x + to.x) * 0.5, (from.y + to.y) * 0.5, 0)
                straight.rotation.z = Math.atan2(directionY, directionX)
                this.container.add(straight)
                this.straights.push(straight)
            }
            route.points.forEach((_point, _index) =>
            {
                if(_index === 0 || _index === route.points.length - 1)
                    return
                const key = `corner:${pointKey(_point)}`
                if(modules.has(key))
                    return
                modules.add(key)
                const isSplitter = this.definition.split && _index === (this.definition.via?.length ?? 0)
                const corner = isSplitter ? this.factory.createBeltSplitter() : this.factory.createBeltCorner()
                corner.position.set(_point.x, _point.y, 0)
                const previous = route.points[_index - 1]
                const next = route.points[_index + 1]
                const incomingX = _point.x - previous.x
                const incomingY = _point.y - previous.y
                const outgoingX = next.x - _point.x
                const outgoingY = next.y - _point.y
                corner.rotation.z = Math.atan2(incomingY, incomingX)
                if(!isSplitter)
                    corner.scale.y = incomingX * outgoingY - incomingY * outgoingX < 0 ? -1 : 1
                this.container.add(corner)
            })
        }

        const endpoints = [this.routes[0]?.points[0], ...this.routes.map((_route) => _route.points.at(-1))]
        for(const point of endpoints.filter(Boolean))
        {
            const key = `plug:${pointKey(point)}`
            if(modules.has(key))
                continue
            modules.add(key)
            const plug = this.factory.createBeltPlug()
            plug.position.set(point.x, point.y, 0)
            this.container.add(plug)
        }
    }

    update(_delta)
    {
        if(this.belt.moving)
            this.travel += _delta * TUNING.beltSpeed
        for(const straight of this.straights)
        {
            const length = straight.userData.length
            for(const slat of straight.userData.slats)
                slat.position.x = - length * 0.5 + ((slat.userData.offset + this.travel) % length)
        }

        const visibleIds = new Set()
        for(let routeIndex = 0; routeIndex < this.routes.length; routeIndex++)
        {
            const route = this.routes[routeIndex]
            for(const item of this.belt.routes[routeIndex])
            {
                visibleIds.add(item.id)
                if(!this.itemViews.has(item.id))
                {
                    const view = this.factory.createItem(this.definition.resource, 0.9)
                    this.itemViews.set(item.id, view)
                    this.container.add(view)
                }
                const position = sampleBeltRoute(route, item.distance)
                const view = this.itemViews.get(item.id)
                view.position.set(position.x, position.y, 0.37)
                view.rotation.z = position.angle
            }
        }
        for(const [id, view] of this.itemViews)
        {
            if(visibleIds.has(id))
                continue
            view.traverse((_child) =>
            {
                if(_child.isMesh)
                    _child.geometry.dispose()
            })
            this.container.remove(view)
            this.itemViews.delete(id)
        }
    }
}
